import React from "react";
import { StructuredText as DatoStructuredText } from "react-datocms";

import { Accordion } from "../Accordion";
import { CookieButton } from "../CookieButton";
import { InlineDocumentArticle } from "../InlineDocumentArticle";
import { InlineDocumentBody } from "../InlineDocumentBody";

import {
  defaultStructuredTextMarkRules,
  defaultStructuredTextNodeRules,
} from "~/common/components/StructuredTextRules";
import { type AccordionPanelTable, type DocumentStructuredText } from "~/types";

interface Props {
  readonly content: DocumentStructuredText;
  readonly initiallyExpandedAccordionPanels?: Array<{
    parentId: string;
    panels: Record<string, boolean>;
  }>;
}

const StructuredText: React.FC<Props> = ({
  content,
  initiallyExpandedAccordionPanels,
}) => {
  return (
    <DatoStructuredText
      data={content}
      customNodeRules={defaultStructuredTextNodeRules}
      customMarkRules={defaultStructuredTextMarkRules}
      renderInlineRecord={({ record }) => {
        switch (record.__typename) {
          case "LegalDocumentArticleRecord": {
            return (
              <InlineDocumentArticle
                title={record.title ?? undefined}
                slug={record.slug ?? undefined}
              />
            );
          }

          case "LegalDocumentAccordionRecord": {
            return (
              <Accordion
                __typename={record.__typename}
                article={record.article}
                body={record.body || ""}
                table={record.table as AccordionPanelTable}
                panels={record.panels.map((panel) => {
                  return {
                    ...panel,
                    body: panel.body || "",
                    table: panel.table as {
                      data: Array<Record<string, string>>;
                      columns: string[];
                    },
                  };
                })}
                id={record.id}
                initialExpanded={record.initialExpanded ?? undefined}
                initiallyExpandedAccordionPanels={initiallyExpandedAccordionPanels?.find(
                  (panel) => {
                    return panel?.parentId === record.id;
                  }
                )}
              />
            );
          }

          case "LegalDocumentBodyRecord": {
            if (!record.body) {
              return null;
            }

            return (
              <InlineDocumentBody
                __typename={record.__typename}
                id={record.id}
                body={record.body}
              />
            );
          }

          case "LegalCookieButtonRecord": {
            return (
              <CookieButton key={record.id} label={record.label ?? undefined} />
            );
          }

          default: {
            return <></>;
          }
        }
      }}
      renderLinkToRecord={({ record, transformedMeta }) => {
        switch (record.__typename) {
          case "LegalCookieButtonRecord": {
            return (
              <CookieButton
                key={record.id}
                label={record.label ?? undefined}
                transformedMeta={transformedMeta ?? undefined}
              />
            );
          }

          default: {
            return <></>;
          }
        }
      }}
    />
  );
};

export default StructuredText;
